import { useEffect, useRef, useState } from 'react';

import { Image, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { TextImageCardFields } from 'src/components/resources/_TextImageCard/TextImageCardFields';

import ChevronDownIcon from 'public/icons/ChevronDown.svg';

import { Button } from '../../basicContent/_Button/Button';
import Transition from '../../basicContent/_Transition/Transition';

import styles from './TextImageCard.module.css';

type TextImageCardProps = TextImageCardFields;

type CardState = {
	isCollapsed?: boolean;
	bodyText: string;
	collapsedMessage?: string;
};

export default function TextImageCard(props: TextImageCardProps): JSX.Element {
	const isEditing = IsSitecoreEditing();

	const container = useRef<HTMLDivElement>(null);
	const content = useRef<HTMLDivElement>(null);

	const [showMoreState, setShowMoreState] = useState(false);
	const [height, setHeight] = useState(0);

	const [cardState, setCardState] = useState<CardState>({
		isCollapsed: false,
		bodyText: '',
		collapsedMessage: '',
	});

	useEffect(() => {
		setCard(props?.Text?.value || '');
	}, []);

	useEffect(() => {
		content?.current && setHeight(content.current.clientHeight);
	}, [cardState]);

	const DESCRIPTION_CHAR_LIMIT = props?.CharacterLimit ? props.CharacterLimit.value : 0;

	const setCard = (bodyText: string) => {
		if (cardState.isCollapsed || isEditing || !DESCRIPTION_CHAR_LIMIT) {
			setCardState({
				isCollapsed: false,
				bodyText: props?.Text?.value || '',
				collapsedMessage: props?.ShowLessText?.value,
			});
		} else {
			if (bodyText.length > DESCRIPTION_CHAR_LIMIT) {
				const shortenedBodyText = bodyText.substring(0, DESCRIPTION_CHAR_LIMIT) + '...';

				setCardState({
					isCollapsed: true,
					bodyText: shortenedBodyText,
					collapsedMessage: props?.ShowMoreText?.value,
				});
			} else {
				setCardState({ bodyText: bodyText });
			}
		}
	};

	if (!isEditing && props.Heading?.value && !props.Text?.value) {
		return <></>;
	}

	return (
		<section className={`${styles.card} shadow`}>
			<Image field={props.Image} />

			<Text tag="h4" field={props.Heading} className={styles.cardHeading} />

			{isEditing ? (
				<RichText className={styles.content} field={props.Text} />
			) : (
				<>
					<Transition
						ref={container}
						duration={300}
						enterState={showMoreState}
						onEntering={() => setCard(props?.Text?.value || '')}
						onExiting={() => setCard(props?.Text?.value || '')}
					>
						<div ref={container} className={`${styles.contentWrapper}`} style={{ height: height ? height + 16 : 'auto' }}>
							<div ref={content} className={styles.content} dangerouslySetInnerHTML={{ __html: cardState.bodyText }}></div>
						</div>
					</Transition>

					{props.CharacterLimit && cardState.collapsedMessage && (
						<Button
							className={`${styles.showMoreButton} ${showMoreState ? 'active' : ''}`}
							onClick={() => setShowMoreState(!showMoreState)}
						>
							<ChevronDownIcon className={styles.chevronIcon} />

							<span>{cardState.collapsedMessage}</span>
						</Button>
					)}
				</>
			)}
		</section>
	);
}
